import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useMusic } from './MusicContext.jsx';
import './MusicBox.css';

export default function MusicBox() {
  const { results, setResults, curIdx, song, play, search, likes, loadLikes } = useMusic();
  useEffect(() => { loadLikes(); }, [loadLikes]); // 进页面再拉一次：别的设备刚收藏的也能看到
  // 歌单做成两张卡片，点开才是一首一首（2026-10-08 铃：歌越来越多页面会越来越长）。
  // 打开哪个歌单记在网址 ?list=sael|likes 上 → 右滑返回/系统返回键都能退回音乐盒首页。
  const [params, setParams] = useSearchParams();
  const list = params.get('list') === 'sael' ? 'sael' : params.get('list') === 'likes' ? 'likes' : '';
  const navigate = useNavigate();
  const location = useLocation();
  const openList = (k) => setParams({ list: k });
  const closeList = () => { if (location.key !== 'default') navigate(-1); else setParams({}, { replace: true }); };
  useEffect(() => { document.querySelector('.app-main')?.scrollTo(0, 0); }, [list]);
  const [q, setQ] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | login | net | empty | done
  const [searched, setSearched] = useState(false);

  // 「他留给你的」信箱
  const [inbox, setInbox] = useState([]);
  const seenPosted = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/api/music/inbox', { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        setInbox(d.songs || []);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);
  // 点开「Sael 留给你的」才算看过 → 清小红点（只清一次；这次打开里 new 标还留着，下次才消）
  const unseen = inbox.filter((s) => !s.seen).length;
  useEffect(() => {
    if (list === 'sael' && unseen > 0 && !seenPosted.current) {
      seenPosted.current = true;
      fetch('/api/music/inbox/seen', { method: 'POST', credentials: 'include' }).catch(() => {});
    }
  }, [list, unseen]);

  const doSearch = useCallback(async () => {
    const kw = q.trim();
    if (!kw) return;
    setSearched(true);
    setState('loading');
    const r = await search(kw);
    if (!r.ok) { setState(r.reason === 'login' ? 'login' : 'net'); return; }
    setResults(r.songs);
    setState(r.songs.length ? 'done' : 'empty');
  }, [q, search, setResults]);

  const onKey = (e) => { if (e.key === 'Enter') { e.target.blur(); doSearch(); } };

  const playLikes = (i) => play(likes.map((s) => ({ id: s.id, name: s.name, artist: s.artist, album: s.album, cover: s.cover })), i);
  const playInbox = (i) => play(inbox.map((s) => ({ id: s.id, name: s.name, artist: s.artist, cover: s.cover })), i);

  const isCur = (s) => song && String(song.id) === String(s.id);

  /* ---------- 点开一个歌单：一首一首 ---------- */
  if (list) {
    const isSael = list === 'sael';
    const arr = isSael ? inbox : likes;
    const playIt = isSael ? playInbox : playLikes;
    return (
      <div className="mb-page">
        <header className="mb-pl-head">
          <button className="mb-back" onClick={closeList} aria-label="回音乐盒">‹</button>
          <div className="mb-pl-titles">
            <div className="mb-pl-title">{isSael ? 'Sael 留给你的' : '我喜欢的'}</div>
            <div className="mb-pl-n">{arr.length} 首</div>
          </div>
          {arr.length > 0 && <button className="mb-likes-all" onClick={() => playIt(0)}>▶ 全部播放</button>}
        </header>

        {arr.length === 0 && (
          <div className="mb-hint">
            {isSael ? '他还没给你留歌～' : <>还没有喜欢的歌<br />听歌时点播放器右下角的 ♡ 就收进来啦</>}
          </div>
        )}

        {isSael ? arr.map((s, i) => (
          <div
            key={s.key || s.id + '_' + i}
            className={'mb-leftcard' + (!s.seen ? ' fresh' : '') + (isCur(s) ? ' playing' : '')}
            onClick={() => playIt(i)}
          >
            {s.cover
              ? <img className="mb-cover" src={s.cover + '?param=120y120'} alt="" loading="lazy" />
              : <div className="mb-cover mb-cover-x" />}
            <div className="mb-meta">
              <div className="mb-nm">{s.name}<span className="mb-lar"> · {s.artist}</span></div>
              {s.note && <div className="mb-note">「{s.note}」</div>}
            </div>
            {!s.seen && <span className="mb-new">new</span>}
          </div>
        )) : (
          <div className="mb-list">
            {arr.map((s, i) => (
              <div key={s.id} className={'mb-row' + (isCur(s) ? ' playing' : '')} onClick={() => playIt(i)}>
                {s.cover
                  ? <img className="mb-cover" src={s.cover + '?param=100y100'} alt="" loading="lazy" />
                  : <div className="mb-cover mb-cover-x" />}
                <div className="mb-meta">
                  <div className="mb-nm">{s.name}</div>
                  <div className="mb-ar">{s.artist}{s.album ? ' · ' + s.album : ''}</div>
                </div>
                {isCur(s) && <div className="mb-eq">♪</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ---------- 音乐盒首页：两张歌单卡 + 搜歌 ---------- */
  const tile = (k, title, arr, badge) => {
    const covers = arr.map((x) => x.cover).filter(Boolean).slice(0, 4);
    return (
      <button className="mb-tile" onClick={() => openList(k)}>
        <div className={'mb-tile-art' + (covers.length >= 4 ? ' grid' : '')}>
          {covers.length >= 4
            ? covers.map((c, i) => <img key={i} src={c + '?param=120y120'} alt="" loading="lazy" />)
            : covers.length
              ? <img src={covers[0] + '?param=240y240'} alt="" loading="lazy" />
              : <span className="mb-tile-empty">{k === 'sael' ? '🎵' : '♡'}</span>}
          {badge > 0 && <span className="mb-tile-badge">{badge}</span>}
        </div>
        <div className="mb-tile-nm">{title}</div>
        <div className="mb-tile-n">{arr.length} 首</div>
      </button>
    );
  };

  return (
    <div className="mb-page">
      <header className="mb-head">
        <h1 className="mb-title">音乐盒</h1>
        <p className="mb-sub">想听什么，搜来放给你听</p>
      </header>

      {/* 搜索框坐最上面（同一般听歌 app）；搜出结果时歌单卡先让开，清空搜索框就回来 */}
      <div className="mb-search">
        <input
          className="mb-input"
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); if (!e.target.value) { setSearched(false); setState('idle'); } }}
          onKeyDown={onKey}
          placeholder="搜歌名 / 歌手…"
          autoComplete="off"
          enterKeyHint="search"
        />
        <button className="mb-go" onClick={doSearch}>搜</button>
      </div>

      {!searched && (
        <div className="mb-tiles">
          {tile('sael', 'Sael 留给你的', inbox, unseen)}
          {tile('likes', '我喜欢的', likes, 0)}
        </div>
      )}

      {!searched && (
        <div className="mb-hint">
          <div className="mb-hint-big">🎧 输入一首想听的歌</div>
          <div className="mb-hint-sm">搜到了点一下就放，<br />它会一直陪着你，去别的页面也不停</div>
        </div>
      )}
      {state === 'loading' && <div className="mb-hint">找找看…</div>}
      {state === 'login' && <div className="mb-hint">先在星洲登录一下就能用啦～</div>}
      {state === 'net' && <div className="mb-hint">网络打了个盹，再搜一次？</div>}
      {state === 'empty' && <div className="mb-hint">没找到这首，换个说法试试？</div>}

      {state === 'done' && (
        <div className="mb-list">
          {results.map((s, i) => (
            <div
              key={s.id + '_' + i}
              className={'mb-row' + (i === curIdx ? ' playing' : '')}
              onClick={() => play(results, i)}
            >
              {s.cover
                ? <img className="mb-cover" src={s.cover + '?param=100y100'} alt="" loading="lazy" />
                : <div className="mb-cover mb-cover-x" />}
              <div className="mb-meta">
                <div className="mb-nm">{s.name}</div>
                <div className="mb-ar">{s.artist}{s.album ? ' · ' + s.album : ''}</div>
              </div>
              {i === curIdx && <div className="mb-eq">♪</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
